import { Info, Languages, X } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { WidgetSettingsDto } from '@/Domain/api/WIdget/dto/WidgetSetting.dto';
import { WidgetTranslationDto } from '@/Domain/api/WIdget/dto/WidgetTranslation.dto';
import { Alert, AlertDescription, AlertTitle } from '@/Domain/components/Shared/Alert/Alert';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { MODAL_NAMES } from '@/Domain/store/modal.store';
import { AddLanguagePopover } from '@/UI/Website/TextContent/AddLanguagePopover';
import { LanguageRail, TranslationTarget } from '@/UI/Website/TextContent/LanguageRail';
import { TranslationEditor } from '@/UI/Website/TextContent/TranslationEditor';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: WidgetSettingsDto;
  translations: WidgetTranslationDto[];
  displayedTranslations: WidgetTranslationDto[];
  selection: TranslationTarget | null;
  onSelect: (selection: TranslationTarget) => void;
  onAdd: (language: string) => void;
  onDelete: (language: string, country: string) => void;
};

export const TranslationsSheet = ({
  open,
  onOpenChange,
  data,
  translations,
  displayedTranslations,
  selection,
  onSelect,
  onAdd,
  onDelete,
}: Props) => {
  const { t } = useTranslation(['website', 'common']);

  const used = useMemo(
    () => new Set(displayedTranslations.map((row) => row.language)),
    [displayedTranslations],
  );

  const targetLabel = selection
    ? `${t(`languages.${selection.language}`, { ns: 'common' })}${
        selection.country ? ` (${selection.country})` : ''
      }`
    : '';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        id={MODAL_NAMES.WIDGET_TRANSLATIONS_SHEET}
        className="w-[92vw] max-w-[1100px] overflow-hidden p-0 sm:max-w-[1100px]"
        data-testid="translations-sheet"
      >
        <div className="flex h-full min-h-0">
          <LanguageRail
            primary={data}
            translations={displayedTranslations}
            selection={selection}
            onSelect={onSelect}
            onAdd={onAdd}
            onDelete={onDelete}
          />

          <div className="flex min-w-0 flex-1 flex-col">
            <SheetHeader className="mb-0 flex-row items-center justify-between border-b border-border px-6 py-4">
              <div className="flex items-center gap-2">
                <Languages className="h-5 w-5 text-foreground-subtle" />
                <SheetTitle>{t('text_content_languages.sheet_title')}</SheetTitle>
              </div>
              <SheetDescription className="sr-only">
                {t('text_content_languages.sheet_description')}
              </SheetDescription>
              <SheetClose asChild>
                <Button variant="ghost" size="icon" aria-label={t('text_content_languages.done')}>
                  <X className="h-5 w-5" />
                </Button>
              </SheetClose>
            </SheetHeader>

            <div className="custom-scroll flex flex-1 flex-col gap-6 overflow-y-auto p-6">
              <Alert variant="info" className="text-foreground [&>svg]:text-info">
                <Info className="h-4 w-4" />
                <AlertTitle>{t('text_content_languages.how_it_works_title')}</AlertTitle>
                <AlertDescription>
                  {t('text_content_languages.how_it_works_description')}
                </AlertDescription>
              </Alert>

              {selection ? (
                <TranslationEditor
                  key={`${selection.language}_${selection.country}`}
                  data={data}
                  language={selection.language}
                  country={selection.country}
                  translations={translations}
                  sourceLabel={t('text_content_languages.source_label')}
                  targetLabel={targetLabel}
                />
              ) : (
                <div
                  className="flex flex-1 flex-col items-center justify-center gap-3 text-center"
                  data-testid="translations-empty-state"
                >
                  <div className="flex flex-col gap-1">
                    <p className="text-sm font-medium text-foreground">
                      {t('text_content_languages.empty_state_title')}
                    </p>
                    <p className="max-w-xs text-sm text-foreground-subtle">
                      {t('text_content_languages.empty_state_description')}
                    </p>
                  </div>
                  <AddLanguagePopover used={used} onAdd={onAdd} />
                </div>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
